import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Redirect } from 'src/routes/Redirect'
import { useIsGamersenseiAffiliate } from 'src/hooks/use-affiliate'
import { Meta, NotFoundMeta } from 'src/system/Meta'
import { useRouting } from 'src/routes'
import { Avatar } from 'src/system/ui/image/Avatar'
import { usePublicProfile } from 'src/hooks/use-profile'
import { AppBar } from 'src/system/app-bar/AppBar'
import { PublicProgressionReport } from 'src/screens/progression-report/ProgressionReportProfile'
import { GamersenseiBranding } from 'src/system/GamersenseiBranding'
import { Background } from 'src/system/ui/layout/Background'
import { PageTitle } from 'src/system/ui/typography/PageTitle'

export function PublicProfile() {
  const { profileId } = useParams<'profileId'>()

  if (profileId == null) {
    return null
  }

  return (
    <>
      <AppBar />
      <Meta name='profile' />
      <Background>
        <Profile profileId={profileId} />
      </Background>
    </>
  )
}

function Profile(props: { profileId: string }) {
  const { toProfile } = useRouting()
  const { profileId } = props

  const { data, loading } = usePublicProfile(profileId)

  if (!data && loading) return <ProfileLoading />
  const id = data?.profile?.id

  if (id != null && id !== profileId) {
    return <Redirect to={toProfile(id)} />
  }

  if (!loading && !data?.profile) return <ProfileNotFoundError />

  return (
    <Container>
      <ProfileInfo profileId={profileId} />
      <GamersenseiBranding profileId={profileId} />
      <PublicProgressionReport profileId={profileId} />
    </Container>
  )
}

function ProfileInfo(props: { profileId: string }) {
  const { data } = usePublicProfile(props.profileId)
  const isGamersenseiAffiliate = useIsGamersenseiAffiliate()
  const namespace = isGamersenseiAffiliate ? 'gamersensei_overview' : 'overview'
  const { t } = useTranslation()
  if (!data) return null
  const steamProfile = data?.profile?.steam
  const name = steamProfile?.name ?? props.profileId
  const image = steamProfile?.avatarUrl
  const titleText = t(`${namespace}:profile:intro`, {
    name
  })
  return (
    <TitleContainer>
      <Meta name='profile' image={image} titleVars={{ name }} />
      <InfoBox>
        <StyledAvatar src={data?.profile?.steam?.avatarUrl} />
        <StyledPageTitle>{titleText}</StyledPageTitle>
      </InfoBox>
    </TitleContainer>
  )
}

function ProfileLoading() {
  const { t } = useTranslation()
  const percent = useFakeProgress()
  return (
    <LoadingBox>
      <LoadingText>{t('overview:profile:loading')}</LoadingText>
      <BarTrack>
        <BarFill style={{ width: `${percent}%` }} />
      </BarTrack>
      <BarPercent>{percent}%</BarPercent>
    </LoadingBox>
  )
}

// Cosmetic "confidence" progress: ramps 0 -> 95% over the first 10s, then +1%
// per second, capped at 99%. The real load isn't measurable; whenever the data
// is ready the parent swaps in the page and this unmounts, so it never finishes.
function useFakeProgress() {
  const [percent, setPercent] = useState(0)

  useEffect(() => {
    const start = Date.now()
    const id = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000
      const next =
        elapsed <= 10 ? (elapsed / 10) * 95 : Math.min(95 + (elapsed - 10), 99)
      setPercent(Math.min(99, Math.round(next)))
    }, 100)
    return () => clearInterval(id)
  }, [])

  return percent
}

function ProfileNotFoundError() {
  const { t } = useTranslation()

  return (
    <>
      <NotFoundMeta />
      <NotFoundErrorText>
        {t('overview:profile:not_found_error')}
      </NotFoundErrorText>
    </>
  )
}

const LoadingBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.theme.d.spacing[6]};
  padding: ${(props) => props.theme.d.spacing[20]} 0;
`

const LoadingText = styled.p`
  color: ${(props) => props.theme.d.colors.text.primary};
  font-size: 18px;
  line-height: 150%;
  text-align: center;
  max-width: 420px;
  margin: 0;
`

const BarTrack = styled.div`
  width: 100%;
  max-width: 420px;
  height: 10px;
  background: ${(props) => props.theme.d.colors.panel.dark};
  border: 1px solid ${(props) => props.theme.d.colors.border.dark};
  border-radius: 999px;
  overflow: hidden;
`

const BarFill = styled.div`
  height: 100%;
  background: ${(props) => props.theme.d.colors.success};
  border-radius: 999px;
  transition: width 0.15s ease;
`

const BarPercent = styled.div`
  color: ${(props) => props.theme.d.colors.text.secondary};
  font-size: 16px;
  font-variant-numeric: tabular-nums;
`

const NotFoundErrorText = styled.div`
  color: ${(props) => props.theme.d.colors.error};
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`

const InfoBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledAvatar = styled(Avatar)`
  display: block;
  height: 64px;
  width: 64px;
  margin-right: 15px;
`

const TitleContainer = styled.div`
  max-width: 1256px;
  margin-left: auto;
  margin-right: auto;
  margin-top: ${(props) => props.theme.d.spacing[10]};
`

const StyledPageTitle = styled(PageTitle)`
  margin-bottom: 0;
`

const Container = styled.div`
  flex: 1;
  flex-direction: column;
  max-width: 1256px;
  margin-left: auto;
  margin-right: auto;
`
